import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingBag, Package, Plus, Minus, Upload, Loader2, Send, Check, Eye,
  X, ClipboardList, FileText, ExternalLink, Clock, ChevronDown, ChevronUp,
  Image, ArrowRight, CreditCard, Truck, AlertCircle, Search
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../services/api';

/* ───── WORKFLOW STEPS (from organizer perspective) ───── */
const STEPS = [
  { key: 'pending', label: 'Envoyee', short: 'Envoi', icon: Send, color: 'amber', hint: 'Votre demande a ete envoyee au prestataire' },
  { key: 'in_progress', label: 'En cours', short: 'Perso', icon: Clock, color: 'blue', hint: 'Le prestataire travaille sur la personnalisation' },
  { key: 'bat_sent', label: 'BAT a valider', short: 'BAT', icon: Image, color: 'violet', hint: 'Verifiez et validez les visuels proposes' },
  { key: 'bat_approved', label: 'BAT valides', short: 'OK', icon: Check, color: 'emerald', hint: 'En attente du devis du prestataire' },
  { key: 'quote_sent', label: 'Devis recu', short: 'Devis', icon: FileText, color: 'orange', hint: 'Consultez et payez le devis' },
  { key: 'deposit_paid', label: 'Acompte paye', short: '50%', icon: CreditCard, color: 'cyan', hint: 'En attente de livraison' },
  { key: 'paid', label: 'Paye', short: 'Paye', icon: Check, color: 'green', hint: 'Commande payee — livraison en cours' },
  { key: 'delivered', label: 'Livre', short: 'Fin', icon: Truck, color: 'slate', hint: 'Vous pouvez publier sur votre evenement' },
];

const stepIndex = (status) => {
  const idx = STEPS.findIndex(s => s.key === status);
  return idx >= 0 ? idx : 0;
};

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
  bat_sent: 'bg-violet-100 text-violet-700 border-violet-200',
  bat_approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  quote_sent: 'bg-orange-100 text-orange-700 border-orange-200',
  deposit_paid: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  paid: 'bg-green-100 text-green-700 border-green-200',
  delivered: 'bg-slate-100 text-slate-700 border-slate-200',
  ready: 'bg-green-100 text-green-700 border-green-200',
};

/* ───── MINI STEPPER ───── */
const MiniStepper = ({ status }) => {
  const current = stepIndex(status);
  return (
    <div className="flex items-center gap-0.5 w-full" data-testid="mini-stepper">
      {STEPS.map((step, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={step.key} className="flex-1 group relative">
            <div className={`h-1.5 sm:h-2 transition-all ${done ? 'bg-green-400' : active ? 'bg-brand' : 'bg-slate-200'}`} />
            {/* Tooltip on hover */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10">
              <div className="bg-asphalt text-white text-[10px] px-2 py-1 whitespace-nowrap rounded-sm shadow-lg">
                {step.label}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ───── FULL STEPPER ───── */
const FullStepper = ({ status }) => {
  const current = stepIndex(status);
  return (
    <div className="flex items-center w-full gap-0 overflow-x-auto pb-1">
      {STEPS.map((step, i) => {
        const done = i < current;
        const active = i === current;
        const Icon = step.icon;
        return (
          <React.Fragment key={step.key}>
            <div className={`flex flex-col items-center min-w-[52px] sm:min-w-[68px] ${active ? 'scale-105' : ''} transition-transform`}>
              <div className={`w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-sm border-2 transition-all
                ${done ? 'bg-green-500 border-green-500 text-white' : active ? 'bg-brand border-brand text-white shadow-md' : 'bg-slate-100 border-slate-200 text-slate-400'}`}>
                {done ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
              </div>
              <span className={`text-[8px] sm:text-[10px] mt-1 text-center leading-tight font-heading uppercase tracking-wide
                ${done ? 'text-green-600 font-bold' : active ? 'text-brand font-bold' : 'text-slate-400'}`}>
                {step.short}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 min-w-[6px] mt-[-10px] ${i < current ? 'bg-green-400' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* ───── MAIN COMPONENT ───── */
const SelectionRequestFlow = ({ events, orgLogo, providerCatalog }) => {
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selections, setSelections] = useState([]);
  const [loadingSelections, setLoadingSelections] = useState(false);
  const [expandedSel, setExpandedSel] = useState(null);
  const [view, setView] = useState('catalog');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchSelections = useCallback(async () => {
    setLoadingSelections(true);
    try {
      const res = await api.get('/organizer/selections');
      setSelections(res.data.selections || []);
    } catch (e) { console.error(e); }
    finally { setLoadingSelections(false); }
  }, []);

  useEffect(() => { fetchSelections(); }, [fetchSelections]);

  const addToCart = (product) => {
    setCart(prev => {
      if (prev.find(c => c.product_id === product.product_id)) return prev;
      return [...prev, {
        product_id: product.product_id,
        name: product.name,
        brand: product.brand || '',
        image_url: product.image_url || '',
        quantity: 10,
        color: '',
        size: '',
        pricing_tiers: product.pricing_tiers || [],
        provider_id: product.provider_id,
      }];
    });
    toast.success(`${product.name} ajoute au panier`);
  };

  const removeFromCart = (productId) => setCart(prev => prev.filter(c => c.product_id !== productId));

  const updateCartItem = (productId, field, value) => {
    setCart(prev => prev.map(c => c.product_id === productId ? { ...c, [field]: value } : c));
  };

  const getApplicablePrice = (item) => {
    const tiers = item.pricing_tiers || [];
    if (!tiers.length) return { unit: 0, label: '' };
    // Sort tiers by min_qty descending to find the best (most specific) match first
    const sorted = [...tiers].sort((a, b) => (b.min_qty || 0) - (a.min_qty || 0));
    for (const t of sorted) {
      if (item.quantity >= (t.min_qty || 0) && (t.max_qty == null || item.quantity <= t.max_qty)) {
        return { unit: t.unit_price, label: t.label || '' };
      }
    }
    // Fallback: lowest tier
    return { unit: tiers[0]?.unit_price || 0, label: tiers[0]?.label || '' };
  };

  const cartTotal = cart.reduce((sum, item) => sum + getApplicablePrice(item).unit * item.quantity, 0);

  const handleSubmitSelection = async () => {
    if (!orgLogo) { toast.error('Importez votre logo HD ci-dessus avant d\'envoyer'); return; }
    if (cart.length === 0) { toast.error('Panier vide'); return; }
    const providerId = cart[0]?.provider_id;
    if (!providerId) { toast.error('Prestataire non identifie'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/organizer/selections', {
        products: cart.map(c => ({ product_id: c.product_id, quantity: c.quantity, color: c.color, size: c.size })),
        logo_url: orgLogo, provider_id: providerId, event_id: selectedEvent, notes,
      });
      toast.success(res.data.message || 'Demande envoyee !');
      setCart([]); setShowCart(false); setNotes(''); setSelectedEvent('');
      fetchSelections(); setView('selections');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur envoi'); }
    finally { setSubmitting(false); }
  };

  const handleApproveBat = async (selectionId) => {
    try {
      await api.put(`/organizer/selections/${selectionId}/approve-bat`);
      toast.success('BAT valides !'); fetchSelections();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const [publishDialog, setPublishDialog] = useState(null); // selection object
  const [publishEventId, setPublishEventId] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [publishStep, setPublishStep] = useState(1); // 1=event, 2=pricing
  const [productsPricing, setProductsPricing] = useState([]); // [{product_id, cost_ttc, selling_price, margin_percent}]

  const MARGIN_OPTIONS = [15, 20, 25, 30, 50, 75, 100];

  const openPublishDialog = (sel) => {
    setPublishDialog(sel);
    setPublishEventId('');
    setPublishStep(1);
    // Initialize pricing from selection products
    const pricing = (sel.products || []).map(p => {
      const costTtc = parseFloat(p.unit_price || 0) * 1.2; // HT to TTC
      const defaultMargin = 30;
      const sellingPrice = Math.round(costTtc * (1 + defaultMargin / 100) * 100) / 100;
      return {
        product_id: p.product_id,
        product_name: p.product_name || '',
        image_url: p.image_url || '',
        quantity: p.quantity || 0,
        cost_ttc: Math.round(costTtc * 100) / 100,
        selling_price: sellingPrice,
        margin_percent: defaultMargin,
        manual: false,
      };
    });
    setProductsPricing(pricing);
  };

  const updateProductPricing = (productId, marginOrPrice, isManual = false) => {
    setProductsPricing(prev => prev.map(p => {
      if (p.product_id !== productId) return p;
      if (isManual) {
        const sp = parseFloat(marginOrPrice) || 0;
        const margin = p.cost_ttc > 0 ? Math.round(((sp / p.cost_ttc) - 1) * 100) : 0;
        return { ...p, selling_price: sp, margin_percent: margin, manual: true };
      }
      const margin = parseFloat(marginOrPrice);
      const sp = Math.round(p.cost_ttc * (1 + margin / 100) * 100) / 100;
      return { ...p, selling_price: sp, margin_percent: margin, manual: false };
    }));
  };

  const handlePublish = async () => {
    if (!publishDialog || !publishEventId) return;
    // Validate all prices > cost
    const invalid = productsPricing.find(p => p.selling_price < p.cost_ttc);
    if (invalid) {
      toast.error(`Le prix de vente de "${invalid.product_name}" doit etre superieur au cout d'achat`);
      return;
    }
    setPublishing(true);
    try {
      const res = await api.post(`/organizer/selections/${publishDialog.selection_id}/publish`, {
        event_id: publishEventId,
        products_pricing: productsPricing.map(p => ({
          product_id: p.product_id,
          selling_price: p.selling_price,
          margin_percent: p.margin_percent,
        })),
      });
      toast.success(res.data.message || 'Produits publies !');
      setPublishDialog(null); setPublishEventId(''); setPublishStep(1);
      setProductsPricing([]); fetchSelections();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur publication'); }
    finally { setPublishing(false); }
  };

  const isInCart = (productId) => cart.some(c => c.product_id === productId);

  const filteredCatalog = providerCatalog.filter(p => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (p.name || '').toLowerCase().includes(s) || (p.brand || '').toLowerCase().includes(s) || (p.category || '').toLowerCase().includes(s);
  });

  return (
    <div data-testid="selection-request-flow" className="space-y-5">

      {/* ── HOW IT WORKS (compact) ── */}
      <div className="bg-asphalt text-white p-4 sm:p-5">
        <h4 className="font-heading font-bold text-sm uppercase tracking-wide mb-2">Comment ca marche ?</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { step: '1', label: 'Selectionnez', desc: 'Choisissez des produits et ajoutez-les au panier', icon: ShoppingBag },
            { step: '2', label: 'Envoyez', desc: 'Soumettez votre demande avec votre logo', icon: Send },
            { step: '3', label: 'Validez', desc: 'Verifiez les BAT et payez le devis', icon: Check },
            { step: '4', label: 'Publiez', desc: 'Mettez en vente sur votre evenement', icon: ShoppingBag },
          ].map((s) => (
            <div key={s.step} className="flex items-start gap-2.5">
              <div className="w-7 h-7 bg-brand text-white flex items-center justify-center font-heading font-black text-sm flex-shrink-0">{s.step}</div>
              <div>
                <p className="text-xs font-heading font-bold uppercase">{s.label}</p>
                <p className="text-[10px] text-slate-400 leading-tight mt-0.5">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── TAB TOGGLE ── */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="flex gap-2">
          <Button
            variant={view === 'catalog' ? 'default' : 'outline'}
            className={`flex-1 sm:flex-initial text-xs font-heading uppercase gap-1.5 h-10 ${view === 'catalog' ? 'bg-brand text-white hover:bg-brand/90' : ''}`}
            onClick={() => setView('catalog')}
            data-testid="view-catalog-btn"
          >
            <ShoppingBag className="w-4 h-4" /> Catalogue
          </Button>
          <Button
            variant={view === 'selections' ? 'default' : 'outline'}
            className={`flex-1 sm:flex-initial text-xs font-heading uppercase gap-1.5 h-10 ${view === 'selections' ? 'bg-brand text-white hover:bg-brand/90' : ''}`}
            onClick={() => setView('selections')}
            data-testid="view-selections-btn"
          >
            <ClipboardList className="w-4 h-4" /> Mes demandes
            {selections.length > 0 && <span className="ml-1 bg-white/20 text-white px-1.5 py-0.5 text-[10px] font-bold">{selections.length}</span>}
          </Button>
        </div>
        {cart.length > 0 && (
          <Button
            className="sm:ml-auto bg-amber-500 hover:bg-amber-600 text-white text-xs font-heading font-bold gap-1.5 h-10 relative"
            onClick={() => setShowCart(!showCart)}
            data-testid="toggle-cart-btn"
          >
            <ShoppingBag className="w-4 h-4" /> Panier ({cart.length})
            <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{cart.length}</span>
          </Button>
        )}
      </div>

      {/* ── CART PANEL ── */}
      <AnimatePresence>
        {showCart && cart.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="border-2 border-amber-300 bg-amber-50/60 overflow-hidden"
          >
            <div className="p-4 sm:p-5">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-heading font-bold text-sm sm:text-base uppercase">Panier de personnalisation</h4>
                <button onClick={() => setShowCart(false)} className="text-slate-400 hover:text-slate-600 p-1"><X className="w-5 h-5" /></button>
              </div>

              {!orgLogo && (
                <div className="bg-red-50 border border-red-300 p-3 mb-4 text-sm text-red-700 font-bold flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  Importez votre logo HD ci-dessus avant d'envoyer votre demande.
                </div>
              )}

              <div className="space-y-3">
                {cart.map((item) => {
                  const price = getApplicablePrice(item);
                  return (
                    <div key={item.product_id} className="bg-white border p-3 sm:p-4 flex gap-3 sm:gap-4" data-testid={`cart-item-${item.product_id}`}>
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-50 flex-shrink-0 overflow-hidden border">
                        {item.image_url ? <img src={item.image_url} alt="" className="w-full h-full object-contain p-1" /> : <Package className="w-8 h-8 text-slate-200 m-auto mt-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h5 className="font-heading font-bold text-xs sm:text-sm leading-tight">{item.name}</h5>
                            {item.brand && <span className="text-[10px] text-slate-400 font-bold uppercase">{item.brand}</span>}
                          </div>
                          <button onClick={() => removeFromCart(item.product_id)} className="text-red-400 hover:text-red-600 p-0.5"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 mt-2">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-500">Qte:</span>
                            <button className="w-7 h-7 border flex items-center justify-center hover:bg-slate-50" onClick={() => updateCartItem(item.product_id, 'quantity', Math.max(1, item.quantity - 1))}><Minus className="w-3 h-3" /></button>
                            <Input
                              type="number" min="1"
                              className="w-16 h-7 text-xs text-center p-0 font-mono"
                              value={item.quantity}
                              onChange={(e) => updateCartItem(item.product_id, 'quantity', parseInt(e.target.value) || 1)}
                            />
                            <button className="w-7 h-7 border flex items-center justify-center hover:bg-slate-50" onClick={() => updateCartItem(item.product_id, 'quantity', item.quantity + 1)}><Plus className="w-3 h-3" /></button>
                          </div>
                          <div className="text-right ml-auto">
                            <span className="font-mono font-bold text-sm text-brand">{(price.unit * item.quantity).toFixed(2)}€</span>
                            <span className="text-[10px] text-slate-400 block">{price.unit.toFixed(2)}€/u{price.label ? ` (${price.label})` : ''}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                <div>
                  <Label className="text-xs font-heading uppercase text-slate-500 mb-1 block">Evenement (optionnel)</Label>
                  <Select value={selectedEvent || 'none'} onValueChange={(v) => setSelectedEvent(v === 'none' ? '' : v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non specifie</SelectItem>
                      {events.map(e => <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-heading uppercase text-slate-500 mb-1 block">Notes pour le prestataire</Label>
                  <Input className="h-9 text-xs" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Instructions, couleurs, emplacement logo..." />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-5 pt-4 border-t border-amber-200 gap-3">
                <div>
                  <span className="text-xs text-slate-500">Total HT estimatif :</span>
                  <span className="font-heading font-black text-2xl text-brand ml-2">{cartTotal.toFixed(2)}€</span>
                  <span className="text-xs text-slate-400 ml-1">(TTC : {(cartTotal * 1.2).toFixed(2)}€)</span>
                </div>
                <Button
                  className="w-full sm:w-auto bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase text-sm gap-2 h-11 px-6"
                  onClick={handleSubmitSelection}
                  disabled={submitting || !orgLogo}
                  data-testid="submit-selection-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Envoyer la demande
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── CATALOG VIEW ── */}
      {view === 'catalog' && (
        <div className="space-y-4">
          {providerCatalog.length > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                className="pl-9 h-10 text-sm"
                placeholder="Rechercher un produit, une marque..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="catalog-search"
              />
            </div>
          )}
          {filteredCatalog.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCatalog.map(pp => (
                <motion.div
                  key={pp.product_id}
                  className={`bg-white border overflow-hidden group hover:border-brand transition-colors ${isInCart(pp.product_id) ? 'border-brand ring-2 ring-brand/20' : 'border-slate-200'}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid={`catalog-product-${pp.product_id}`}
                >
                  <div className="relative h-44 sm:h-52 bg-slate-50 flex items-center justify-center overflow-hidden">
                    {pp.image_url ? <img src={pp.image_url} alt={pp.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <Package className="w-14 h-14 text-slate-200" />}
                    <div className="absolute top-3 left-3"><span className="bg-violet-600 text-white px-2.5 py-1 text-[10px] font-bold uppercase">{pp.category}</span></div>
                    {isInCart(pp.product_id) && <div className="absolute top-3 right-3"><span className="bg-brand text-white px-2 py-1 text-[10px] font-bold flex items-center gap-1"><Check className="w-3 h-3" /> Panier</span></div>}
                  </div>
                  <div className="p-4">
                    <h4 className="font-heading font-bold text-sm">{pp.name}</h4>
                    {pp.brand && <span className="text-[10px] text-slate-400 font-bold uppercase block">{pp.brand}</span>}
                    {pp.description && <p className="text-xs text-slate-500 line-clamp-2 mt-1.5 mb-2">{pp.description}</p>}
                    {pp.sizes?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {pp.sizes.slice(0, 6).map(s => <span key={s} className="bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold">{s}</span>)}
                        {pp.sizes.length > 6 && <span className="text-[10px] text-slate-400">+{pp.sizes.length - 6}</span>}
                      </div>
                    )}
                    {pp.pricing_tiers?.length > 0 && (
                      <div className="mb-3 bg-amber-50 border border-amber-200/60 px-3 py-2">
                        <span className="text-[10px] font-heading uppercase text-amber-600 font-bold block mb-1">Tarifs degressifs</span>
                        {pp.pricing_tiers.map((t, i) => (
                          <div key={i} className="text-xs flex justify-between">
                            <span className="text-slate-600">{t.min_qty}{t.max_qty ? `-${t.max_qty}` : '+'} pcs</span>
                            <span className="font-mono font-bold text-amber-700">{parseFloat(t.unit_price).toFixed(2)}€/u</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {isInCart(pp.product_id) ? (
                      <Button variant="outline" size="sm" className="w-full h-10 text-xs text-red-500 border-red-200 hover:bg-red-50" onClick={() => removeFromCart(pp.product_id)}>
                        <X className="w-3.5 h-3.5 mr-1" /> Retirer du panier
                      </Button>
                    ) : (
                      <Button size="sm" className="w-full h-10 text-xs bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase gap-1.5" onClick={() => addToCart(pp)} data-testid={`add-to-cart-${pp.product_id}`}>
                        <Plus className="w-4 h-4" /> Ajouter au panier
                      </Button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="bg-white border border-dashed border-slate-300 p-12 sm:p-16 text-center">
              <Package className="w-14 h-14 mx-auto mb-4 text-slate-200" />
              <h3 className="font-heading font-bold text-base uppercase mb-1">{searchTerm ? 'Aucun resultat' : 'Aucun produit'}</h3>
              <p className="text-sm text-slate-400">{searchTerm ? 'Essayez un autre terme de recherche.' : 'Aucun prestataire n\'a encore ajoute de produits.'}</p>
            </div>
          )}
        </div>
      )}

      {/* ── SELECTIONS VIEW ── */}
      {view === 'selections' && (
        <div>
          {loadingSelections ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>
          ) : selections.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 p-12 sm:p-16 text-center">
              <ClipboardList className="w-14 h-14 mx-auto mb-4 text-slate-200" />
              <h3 className="font-heading font-bold text-base uppercase mb-1">Aucune demande</h3>
              <p className="text-sm text-slate-400">Selectionnez des produits dans le catalogue et envoyez votre demande.</p>
              <Button className="mt-4 bg-brand hover:bg-brand/90 text-white text-xs font-heading uppercase gap-1" onClick={() => setView('catalog')}>
                <ShoppingBag className="w-4 h-4" /> Voir le catalogue
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {selections.map((sel) => {
                const isExpanded = expandedSel === sel.selection_id;
                const current = stepIndex(sel.status);
                const stepInfo = STEPS[current] || STEPS[0];
                const StepIcon = stepInfo.icon;
                const statusColor = STATUS_COLORS[sel.status] || STATUS_COLORS.pending;

                return (
                  <motion.div
                    key={sel.selection_id}
                    className="bg-white border border-slate-200 overflow-hidden hover:border-slate-300 transition-colors"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid={`selection-${sel.selection_id}`}
                  >
                    {/* Progress bar */}
                    <MiniStepper status={sel.status} />

                    {/* Header */}
                    <div className="p-4 sm:p-5 cursor-pointer hover:bg-slate-50/50 transition-colors" onClick={() => setExpandedSel(isExpanded ? null : sel.selection_id)}>
                      <div className="flex items-start gap-3 sm:gap-4">
                        {sel.logo_url ? (
                          <img src={sel.logo_url} alt="Logo" className="w-11 h-11 sm:w-14 sm:h-14 object-contain border bg-white flex-shrink-0" />
                        ) : (
                          <div className="w-11 h-11 sm:w-14 sm:h-14 bg-slate-100 flex items-center justify-center flex-shrink-0"><Package className="w-6 h-6 text-slate-300" /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 border ${statusColor}`}>
                              <StepIcon className="w-3.5 h-3.5" /> {stepInfo.label}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
                            <span className="font-heading font-bold text-slate-700">{sel.products?.length || 0} produit(s)</span>
                            <span className="font-heading font-bold text-brand">{sel.quote?.total_ttc?.toFixed(2) || '0.00'}€ TTC</span>
                            <span>{sel.created_at ? new Date(sel.created_at).toLocaleDateString('fr-FR') : ''}</span>
                          </div>
                          {/* Hint */}
                          {stepInfo.hint && sel.status !== 'delivered' && !sel.published_to_event && (
                            <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1.5">
                              <ArrowRight className="w-3.5 h-3.5 text-brand flex-shrink-0" /> {stepInfo.hint}
                            </p>
                          )}
                          {sel.published_to_event && (
                            <p className="text-xs text-green-600 font-bold mt-1.5 flex items-center gap-1"><Check className="w-4 h-4" /> Publie sur votre evenement</p>
                          )}
                        </div>
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />}
                      </div>
                    </div>

                    {/* Expanded */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 sm:px-5 pb-5 border-t border-slate-100 pt-4 space-y-5">
                            {/* Full stepper */}
                            <FullStepper status={sel.status} />

                            {/* Products */}
                            <div>
                              <h5 className="font-heading font-bold text-xs uppercase text-slate-400 mb-2 tracking-wide">Produits</h5>
                              <div className="space-y-2">
                                {sel.products?.map((p, i) => (
                                  <div key={i} className="flex items-center gap-3 sm:gap-4 bg-white border border-slate-200 p-3 sm:p-4">
                                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-slate-50 border overflow-hidden flex-shrink-0">
                                      {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-contain p-1" /> : <Package className="w-6 h-6 text-slate-200 m-auto mt-4" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-heading font-bold truncate">{p.product_name}</p>
                                      <p className="text-xs text-slate-500">{p.quantity} x {p.unit_price?.toFixed(2)}€/u{p.tier_label && ` (${p.tier_label})`}</p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                      <span className="font-mono font-bold text-sm">{p.line_total?.toFixed(2)}€</span>
                                      {p.bat_images?.length > 0 && (
                                        <span className="block text-[10px] text-emerald-600 font-bold mt-0.5"><Check className="w-3 h-3 inline" /> BAT pret</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Quote */}
                            {sel.quote && (
                              <div className="bg-asphalt text-white p-4 sm:p-5">
                                <h5 className="font-heading font-bold text-xs uppercase tracking-wide text-slate-400 mb-3">Devis</h5>
                                <div className="space-y-1.5">
                                  <div className="flex justify-between text-sm"><span>Total HT</span><span className="font-mono font-bold">{sel.quote.total_ht?.toFixed(2)}€</span></div>
                                  <div className="flex justify-between text-xs text-slate-400"><span>TVA ({sel.quote.tva_rate}%)</span><span className="font-mono">{sel.quote.tva_amount?.toFixed(2)}€</span></div>
                                  <div className="flex justify-between text-lg font-bold border-t border-slate-600 pt-2 mt-2"><span className="font-heading uppercase">Total TTC</span><span className="font-heading text-brand">{sel.quote.total_ttc?.toFixed(2)}€</span></div>
                                </div>
                              </div>
                            )}

                            {/* Payment info */}
                            {sel.payment_mode && (
                              <div className="bg-blue-50 border border-blue-200 p-4">
                                <h5 className="font-heading font-bold text-xs uppercase mb-2">Paiement</h5>
                                <p className="text-sm">Mode : <strong>{sel.payment_mode === '50_50' ? '50% a la commande, solde a la livraison' : 'Paiement integral'}</strong></p>
                                {sel.payment_link && (
                                  <a href={sel.payment_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-brand font-bold mt-2 hover:underline" data-testid="payment-link">
                                    <ExternalLink className="w-4 h-4" /> Payer via SumUp
                                  </a>
                                )}
                                <div className="flex flex-wrap gap-3 mt-2 text-xs">
                                  {sel.deposit_paid && <span className="text-green-600 font-bold flex items-center gap-1"><Check className="w-4 h-4" /> Acompte paye</span>}
                                  {sel.fully_paid && <span className="text-green-600 font-bold flex items-center gap-1"><Check className="w-4 h-4" /> Integralement paye</span>}
                                </div>
                              </div>
                            )}

                            {/* BAT viewer */}
                            {sel.products?.some(p => p.bat_images?.length > 0) && (
                              <div>
                                <h5 className="font-heading font-bold text-xs uppercase text-slate-400 mb-2 tracking-wide">BAT (Bons a Tirer)</h5>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                  {sel.products.filter(p => p.bat_images?.length > 0).map((p, i) => (
                                    <div key={i} className="text-center">
                                      <div className="aspect-square bg-slate-50 border overflow-hidden mb-1.5">
                                        <img src={p.bat_images[0]} alt={p.product_name} className="w-full h-full object-contain p-2" />
                                      </div>
                                      <span className="text-[10px] text-slate-500 line-clamp-1">{p.product_name}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Actions */}
                            <div className="space-y-2 pt-2 border-t border-slate-100">
                              {sel.status === 'bat_sent' && (
                                <Button className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white font-heading font-bold uppercase text-sm gap-2 h-11 px-6" onClick={() => handleApproveBat(sel.selection_id)} data-testid={`approve-bat-${sel.selection_id}`}>
                                  <Check className="w-4 h-4" /> Valider les BAT
                                </Button>
                              )}

                              {['paid', 'delivered', 'deposit_paid', 'quote_sent'].includes(sel.status) && !sel.published_to_event && (
                                <Button className="w-full sm:w-auto bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase text-sm gap-2 h-11 px-6" onClick={() => openPublishDialog(sel)} data-testid={`publish-to-event-${sel.selection_id}`}>
                                  <ShoppingBag className="w-4 h-4" /> Publier sur un evenement
                                </Button>
                              )}

                              {sel.notes && (
                                <p className="text-xs text-slate-400 italic mt-2">Vos notes : {sel.notes}</p>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── PUBLISH DIALOG WITH PRICING ── */}
      <Dialog open={!!publishDialog} onOpenChange={(open) => { if (!open) { setPublishDialog(null); setPublishStep(1); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-base">
              {publishStep === 1 ? 'Etape 1 — Choisir l\'evenement' : 'Etape 2 — Definir vos prix de vente'}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {publishStep === 1
                ? 'Selectionnez l\'evenement sur lequel mettre en vente vos produits personnalises.'
                : 'Definissez le prix de vente TTC pour chaque produit. Une commission de 5% sera prelevee par iBookSport sur chaque vente.'
              }
            </DialogDescription>
          </DialogHeader>

          {/* Step indicator */}
          <div className="flex items-center gap-2 my-2">
            <div className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-heading font-bold uppercase ${publishStep === 1 ? 'bg-brand text-white' : 'bg-green-100 text-green-700'}`}>
              {publishStep > 1 ? <Check className="w-3.5 h-3.5" /> : '1.'} Evenement
            </div>
            <ArrowRight className="w-4 h-4 text-slate-300" />
            <div className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-heading font-bold uppercase ${publishStep === 2 ? 'bg-brand text-white' : 'bg-slate-100 text-slate-400'}`}>
              2. Prix de vente
            </div>
          </div>

          {/* STEP 1: Event Selection */}
          {publishStep === 1 && (
            <div className="space-y-4 pt-2">
              <div>
                <Label className="text-xs font-heading uppercase text-slate-500 mb-2 block">Evenement</Label>
                <Select value={publishEventId || 'none'} onValueChange={(v) => setPublishEventId(v === 'none' ? '' : v)}>
                  <SelectTrigger className="h-10"><SelectValue placeholder="Choisir un evenement..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>Choisir...</SelectItem>
                    {events.map(e => <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button
                className="w-full bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase text-sm gap-2 h-11"
                onClick={() => setPublishStep(2)}
                disabled={!publishEventId}
                data-testid="publish-step-next"
              >
                Suivant — Definir les prix <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* STEP 2: Pricing */}
          {publishStep === 2 && (
            <div className="space-y-4 pt-2">
              {/* Commission warning */}
              <div className="bg-amber-50 border-2 border-amber-300 p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-heading font-bold text-amber-800">Commission iBookSport : 5%</p>
                  <p className="text-xs text-amber-700 mt-0.5">Sur chaque produit vendu sur www.ibooksport.com, une commission de 5% du prix de vente TTC sera prelevee. Ex : produit vendu 30€ → 1,50€ commission → 28,50€ net pour vous.</p>
                </div>
              </div>

              {/* Products pricing */}
              <div className="space-y-4">
                {productsPricing.map((p) => {
                  const commission5 = Math.round(p.selling_price * 0.05 * 100) / 100;
                  const netVendeur = Math.round((p.selling_price - commission5) * 100) / 100;
                  const benefice = Math.round((netVendeur - p.cost_ttc) * 100) / 100;
                  return (
                    <div key={p.product_id} className="border border-slate-200 p-4" data-testid={`pricing-${p.product_id}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-slate-50 border overflow-hidden flex-shrink-0">
                          {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-contain p-1" /> : <Package className="w-6 h-6 text-slate-300 m-auto mt-3" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-heading font-bold text-sm truncate">{p.product_name}</p>
                          <p className="text-xs text-slate-500">Quantite : {p.quantity} — Cout d'achat TTC : <strong className="text-slate-700">{p.cost_ttc.toFixed(2)}€</strong>/u</p>
                        </div>
                      </div>

                      {/* Margin buttons */}
                      <div className="mb-3">
                        <Label className="text-[10px] font-heading uppercase text-slate-400 mb-1.5 block">Marge souhaitee</Label>
                        <div className="flex flex-wrap gap-1.5">
                          {MARGIN_OPTIONS.map(m => (
                            <button
                              key={m}
                              className={`px-3 py-1.5 text-xs font-heading font-bold border transition-all ${!p.manual && p.margin_percent === m ? 'bg-brand text-white border-brand' : 'bg-white text-slate-600 border-slate-200 hover:border-brand hover:text-brand'}`}
                              onClick={() => updateProductPricing(p.product_id, m)}
                              data-testid={`margin-${m}-${p.product_id}`}
                            >
                              +{m}%
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Manual price */}
                      <div className="flex items-center gap-3 mb-3">
                        <Label className="text-xs text-slate-500 whitespace-nowrap">Ou prix manuel TTC :</Label>
                        <div className="relative flex-1 max-w-[150px]">
                          <Input
                            type="number" step="0.01" min={p.cost_ttc}
                            className="h-9 text-sm font-mono pr-6"
                            value={p.manual ? p.selling_price : ''}
                            placeholder={p.selling_price.toFixed(2)}
                            onChange={(e) => updateProductPricing(p.product_id, e.target.value, true)}
                            data-testid={`manual-price-${p.product_id}`}
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span>
                        </div>
                      </div>

                      {/* Pricing summary */}
                      <div className="bg-slate-50 border border-slate-100 p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                        <div>
                          <p className="text-[10px] font-heading uppercase text-slate-400">Prix de vente TTC</p>
                          <p className="font-heading font-black text-lg text-brand">{p.selling_price.toFixed(2)}€</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-heading uppercase text-slate-400">Commission 5%</p>
                          <p className="font-heading font-bold text-base text-red-500">-{commission5.toFixed(2)}€</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-heading uppercase text-slate-400">Net vendeur</p>
                          <p className="font-heading font-bold text-base text-slate-700">{netVendeur.toFixed(2)}€</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-heading uppercase text-slate-400">Benefice / unite</p>
                          <p className={`font-heading font-black text-lg ${benefice >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {benefice >= 0 ? '+' : ''}{benefice.toFixed(2)}€
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Total summary */}
              {productsPricing.length > 0 && (
                <div className="bg-asphalt text-white p-4 sm:p-5">
                  <h5 className="font-heading font-bold text-xs uppercase tracking-wide text-slate-400 mb-3">Resume de votre publication</h5>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-[10px] font-heading uppercase text-slate-400">Chiffre d'affaires potentiel</p>
                      <p className="font-heading font-black text-xl text-brand">
                        {productsPricing.reduce((s, p) => s + p.selling_price * p.quantity, 0).toFixed(2)}€
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-heading uppercase text-slate-400">Commission 5% iBookSport</p>
                      <p className="font-heading font-bold text-lg text-red-400">
                        -{productsPricing.reduce((s, p) => s + Math.round(p.selling_price * 0.05 * 100) / 100 * p.quantity, 0).toFixed(2)}€
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-heading uppercase text-slate-400">Benefice net total</p>
                      <p className="font-heading font-black text-xl text-green-400">
                        +{productsPricing.reduce((s, p) => {
                          const net = p.selling_price * 0.95 - p.cost_ttc;
                          return s + net * p.quantity;
                        }, 0).toFixed(2)}€
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1 h-11 text-sm font-heading uppercase" onClick={() => setPublishStep(1)}>
                  Retour
                </Button>
                <Button
                  className="flex-1 bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase text-sm gap-2 h-11"
                  onClick={handlePublish}
                  disabled={publishing || productsPricing.some(p => p.selling_price < p.cost_ttc)}
                  data-testid="confirm-publish-btn"
                >
                  {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
                  Publier les produits
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SelectionRequestFlow;
